import React, { useLayoutEffect, useRef } from "react";
import { ArrowUp, Loader2, Paperclip } from "lucide-react";
import { GenericTag } from "@ui-components/Tag/GenericTag";

type Props = {
  value: string;
  onChange: (value: string) => void;
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** Only the Kontrol Edge reply endpoint accepts attachments. */
  allowAttachments: boolean;
  onSend: () => void;
  isSending: boolean;
  /** False when the modal was opened without a way to send replies. */
  canSend: boolean;
};

/** Tallest the text area grows before it scrolls, about six lines. */
const MAX_TEXTAREA_HEIGHT_PX = 144;

/**
 * Reply composer of the ticket detail modal, shaped like a chat input
 * (CTX-1121): one line that grows with the text, attach and send as icons in
 * the same box, Enter to send and Shift+Enter for a new line.
 */
export function TicketReplyBox({
  value,
  onChange,
  files,
  onFilesChange,
  allowAttachments,
  onSend,
  isSending,
  canSend,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sendDisabled = !value.trim() || !canSend || isSending;

  // Grow with the text, then scroll; shrink back once the reply is sent.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter breaks the line; never mid IME composition.
    if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    if (!sendDisabled) onSend();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) onFilesChange(Array.from(e.target.files));
  };

  return (
    <div className="rounded-lg border border-semantic-border-secondary bg-semantic-bg-primary transition-colors focus-within:border-semantic-border-brand-primary">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pt-3">
          {files.map((file, i) => (
            <GenericTag
              key={`${file.name}-${i}`}
              label={file.name}
              variant="neutral"
              size="small"
              removable
              onRemove={() => onFilesChange(files.filter((_, j) => j !== i))}
            />
          ))}
        </div>
      )}

      <label htmlFor="reply-input" className="sr-only">
        Reply
      </label>
      <textarea
        id="reply-input"
        ref={textareaRef}
        rows={1}
        className="block w-full resize-none border-0 bg-transparent px-3 pt-2.5 text-sm text-semantic-text-primary outline-none placeholder:text-semantic-text-secondary focus:ring-0"
        style={{ maxHeight: MAX_TEXTAREA_HEIGHT_PX }}
        placeholder="Write a reply…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />

      <div className="flex items-center justify-between gap-2 px-2 pb-2">
        <div className="flex items-center gap-1">
          {allowAttachments && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept="*/*"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                aria-label="Attach files"
                title="Attach files"
                className="flex h-8 w-8 items-center justify-center rounded-md text-semantic-text-secondary transition-colors hover:bg-semantic-state-active hover:text-semantic-text-primary"
              >
                <Paperclip className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          )}
          <span className="hidden text-xs text-semantic-text-secondary sm:inline">
            Enter to send · Shift+Enter for a new line
          </span>
        </div>
        <button
          type="button"
          onClick={onSend}
          disabled={sendDisabled}
          aria-label="Send reply"
          title="Send reply"
          className="flex h-8 w-8 items-center justify-center rounded-md bg-semantic-bg-brand-bold-secondary text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <ArrowUp className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
}
